import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, FormInstance } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { useGetSizeCodesQuery } from 'entities/SizeCode';
import _ from 'lodash';
import { CheckboxGroup } from 'shared/ui/CheckboxGroup';

interface UnitSizesFormProps {
  formInstance: FormInstance;
  unitSizes?: Nullable<string[]>;
  handleClose: () => void;
}

export const UnitSizesForm: FC<UnitSizesFormProps> = memo((props) => {
  const { unitSizes, handleClose, formInstance } = props;

  const { t } = useAppTranslation('contracts');
  const [form] = Form.useForm();

  const [warehouseIds, setWarehouseIds] = useState<Nullable<string[]>>(null);

  const { data: sizeCodes } = useGetSizeCodesQuery({
    warehouseId: warehouseIds || undefined,
  });

  const sizeOptions = useMemo(() => {
    if (!sizeCodes) {
      return [];
    }

    const uniqueBySquare = _.uniqBy(sizeCodes, 'square');

    return uniqueBySquare.map((sizeCode) => ({
      label: sizeCode.square,
      value: sizeCode.square,
    }));
  }, [sizeCodes]);

  useEffect(() => {
    const formWarehouseIds = formInstance.getFieldValue('warehouseIds');
    const warehouseIds = formWarehouseIds?.length > 0 ? formWarehouseIds : null;

    setWarehouseIds(warehouseIds);
  }, [formInstance, unitSizes]);

  useEffect(() => {
    if (unitSizes && sizeOptions.length > 0) {
      const checkedUnitSizes = unitSizes.map((size) => Number(size));

      form.setFieldsValue({ unitSizes: checkedUnitSizes });
    }
  }, [unitSizes, sizeOptions, form]);

  const handleSubmit = useCallback(
    async ({ unitSizes }: { unitSizes: string[] }): Promise<void> => {
      formInstance.setFieldsValue({ unitSizes });
      handleClose();
    },
    [formInstance, handleClose],
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center py-5">
        <div className="font-semibold text-3xl">{t('Unit sizes')}</div>
        <div className="text-primaryLight font-normal cursor-pointer" onClick={handleClose}>
          {t('Close')}
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="unitSizes">
          <CheckboxGroup name="unitSizes" options={sizeOptions} className="grid grid-cols-6 gap-4" />
        </Form.Item>

        <Button type="submit" className="mt-10">
          {t('Save')}
        </Button>
      </Form>
    </div>
  );
});
